/**
 * UniversityTopNav — public KISSED University navigation with dropdown menus.
 * Drop-in replacement for the long inline link row.
 *
 *   <UniversityTopNav />
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";

const MENU = [
  { label: "Home", to: "/university" },
  {
    label: "Admissions",
    items: [
      { label: "Pathway Quiz", to: "/university/quiz" },
      { label: "Apply", to: "/university/apply" },
      { label: "Open Enrollment", to: "/university/enroll" },
      { label: "Check Application Status", to: "/university/status" },
    ],
  },
  {
    label: "Programs",
    items: [
      { label: "College of Trichology Science", to: "/university/college/trichology" },
      { label: "Clinical Residency", to: "/university/residency" },
      { label: "College of Herbal Medicine", to: "/university/college/herbal" },
      { label: "Holistic Health & Functional Wellness", to: "/university/college/hhfw" },
      { label: "Continuing Education", to: "/university/ce-calendar" },
      { label: "Doctor of Trichology Science", to: "/university/program/dts" },
    ],
  },
  {
    label: "Students",
    items: [
      { label: "Student Portal", to: "/university/student" },
      { label: "My Enrollments", to: "/university/my-enrollments" },
      { label: "CE Calendar", to: "/university/ce-calendar" },
      { label: "Clinical Residency", to: "/university/residency" },
    ],
  },
  {
    label: "Community",
    items: [{ label: "University Community", to: "/university/community" }],
  },
  {
    label: "Alumni",
    items: [
      { label: "Alumni Directory", to: "/university/alumni" },
      { label: "Registry", to: "/university/registry" },
      { label: "Board", to: "/university/board" },
    ],
  },
  { label: "About", to: "/university/about" },
];

export default function UniversityTopNav() {
  const [open, setOpen] = useState(null); // hovered/clicked dropdown label
  const [mobile, setMobile] = useState(false);

  return (
    <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30" data-testid="uni-top-nav">
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/university" className="flex items-center gap-3" data-testid="uni-nav-brand">
          <img src={logo} alt="KISSED" className="h-10 w-auto" />
          <span className="font-display text-base text-slate-800 hidden sm:block">KISSED University</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {MENU.map((m) => (
            m.items ? (
              <div key={m.label} className="relative" onMouseEnter={() => setOpen(m.label)} onMouseLeave={() => setOpen(null)}>
                <button className="px-3 py-2 text-sm text-slate-700 hover:text-purple-900 inline-flex items-center gap-1" data-testid={`uni-nav-${m.label.toLowerCase()}`}>
                  {m.label}<ChevronDown className="w-3 h-3 opacity-60" />
                </button>
                {open === m.label && (
                  <div className="absolute left-0 top-full pt-2 min-w-[240px]" data-testid={`uni-dropdown-${m.label.toLowerCase()}`}>
                    <div className="bg-white border border-slate-200 rounded-xl shadow-lg py-2">
                      {m.items.map((it) => (
                        <Link key={it.to + it.label} to={it.to} className="block px-4 py-2 text-sm text-slate-700 hover:bg-purple-50 hover:text-purple-900" onClick={() => setOpen(null)} data-testid={`uni-nav-item-${it.label.toLowerCase().replace(/\W+/g, "-")}`}>
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link key={m.label} to={m.to} className="px-3 py-2 text-sm text-slate-700 hover:text-purple-900" data-testid={`uni-nav-${m.label.toLowerCase()}`}>
                {m.label}
              </Link>
            )
          ))}
        </nav>

        <button className="lg:hidden p-2" onClick={() => setMobile(true)} aria-label="Open menu" data-testid="uni-nav-mobile-open">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobile && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="uni-nav-mobile">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobile(false)} />
          <aside className="relative w-80 bg-white h-full overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <span className="font-display text-sm">Menu</span>
              <button onClick={() => setMobile(false)} aria-label="Close" data-testid="uni-nav-mobile-close"><X className="w-4 h-4" /></button>
            </div>
            <nav className="py-2">
              {MENU.map((m) => (
                m.items ? (
                  <details key={m.label} className="border-b border-slate-100">
                    <summary className="px-5 py-3 text-sm text-slate-800 cursor-pointer flex items-center justify-between">
                      <span>{m.label}</span><ChevronDown className="w-3.5 h-3.5" />
                    </summary>
                    <div className="pb-2 bg-slate-50/60">
                      {m.items.map((it) => (
                        <Link key={it.to + it.label} to={it.to} onClick={() => setMobile(false)} className="block pl-8 pr-5 py-2 text-sm text-slate-700">{it.label}</Link>
                      ))}
                    </div>
                  </details>
                ) : (
                  <Link key={m.label} to={m.to} onClick={() => setMobile(false)} className="block px-5 py-3 text-sm text-slate-800 border-b border-slate-100">{m.label}</Link>
                )
              ))}
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
}
